import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PersistenceService } from '../../../shared/persistence/services/persistence.service';
import {
  AccessMatrixService,
  RouteTypeEnum,
} from '../../../../lib/services/access-matrix.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import bannerBookmarks from '../../../../assets/rsrc/banners.json';

interface BannerManagerForm {
  frLogin: FormControl<string>;
  frApplication: FormControl<string>;
  frFreeMedispring: FormControl<string>;
  nlLogin: FormControl<string>;
  nlApplication: FormControl<string>;
  nlFreeMedispring: FormControl<string>;
}

interface BannerField {
  controlName: keyof BannerManagerForm;
  label: string;
}

interface BannerSection {
  id: string;
  title: string;
  fields: BannerField[];
}

interface BannerContentDefinition {
  controlName: keyof BannerManagerForm;
  filePath: string;
}

interface BannerContentState {
  filePath: string;
  content: string;
  isValid: boolean;
  normalizedContent: string;
}

interface BannerBookmarkLanguage {
  login: string;
  app: string;
  free: string;
}

interface BannerBookmark {
  title: string;
  languages: {
    fr: BannerBookmarkLanguage;
    nl: BannerBookmarkLanguage;
  };
}

interface BannerBookmarksFile {
  bookmarks: BannerBookmark[];
}

@Component({
  selector: 'ms-banner-manager',
  templateUrl: './banner-manager.component.html',
  styleUrls: ['./banner-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class BannerManagerComponent implements OnInit {
  public readonly bookmarks: BannerBookmark[] = (
    bannerBookmarks as BannerBookmarksFile
  ).bookmarks;

  private readonly bannerContentDefinitions: BannerContentDefinition[] = [
    {
      controlName: 'frLogin',
      filePath: 'banner/fr/medispring-banner-login.html',
    },
    {
      controlName: 'frApplication',
      filePath: 'banner/fr/medispring-banner-kraken.html',
    },
    {
      controlName: 'frFreeMedispring',
      filePath: 'banner/fr/medispring-banner-free.html',
    },
    {
      controlName: 'nlLogin',
      filePath: 'banner/nl/medispring-banner-login.html',
    },
    {
      controlName: 'nlApplication',
      filePath: 'banner/nl/medispring-banner-kraken.html',
    },
    {
      controlName: 'nlFreeMedispring',
      filePath: 'banner/nl/medispring-banner-free.html',
    },
  ];

  constructor(
    private readonly persistenceService: PersistenceService,
    private readonly accessMatrixService: AccessMatrixService,
    private readonly router: Router,
  ) {}

  public readonly form: FormGroup<BannerManagerForm> = new FormGroup<BannerManagerForm>({
    frLogin: new FormControl<string>('', { nonNullable: true }),
    frApplication: new FormControl<string>('', { nonNullable: true }),
    frFreeMedispring: new FormControl<string>('', { nonNullable: true }),
    nlLogin: new FormControl<string>('', { nonNullable: true }),
    nlApplication: new FormControl<string>('', { nonNullable: true }),
    nlFreeMedispring: new FormControl<string>('', { nonNullable: true }),
  });

  public readonly sections: BannerSection[] = [
    {
      id: 'fr',
      title: 'Français',
      fields: [
        { controlName: 'frLogin', label: 'Login' },
        { controlName: 'frApplication', label: 'Application' },
        { controlName: 'frFreeMedispring', label: 'Free Medispring' },
      ],
    },
    {
      id: 'nl',
      title: 'Néerlandais',
      fields: [
        { controlName: 'nlLogin', label: 'Login' },
        { controlName: 'nlApplication', label: 'Application' },
        { controlName: 'nlFreeMedispring', label: 'Free Medispring' },
      ],
    },
  ];

  public readonly saveError = signal<string | null>(null);

  public async ngOnInit(): Promise<void> {
    if (!this.accessMatrixService.isAvailableModule(RouteTypeEnum.BANNER)) {
      await this.router.navigate(['/']);
      return;
    }

    await Promise.all(
      this.bannerContentDefinitions.map(async (definition: BannerContentDefinition): Promise<void> => {
        const content: string = await this.persistenceService.getPublicFileText(
          definition.filePath,
        );

        this.form.controls[definition.controlName].setValue(content);
      }),
    );

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  public async saveBanner(): Promise<void> {
    const contentStates: BannerContentState[] =
      this.bannerContentDefinitions.map((definition: BannerContentDefinition) => ({
        filePath: definition.filePath,
        content: this.form.controls[definition.controlName].value,
        ...this.validateAndNormalizeContent(
          this.form.controls[definition.controlName].value,
        ),
      }));

    this.applyValidationState(contentStates);

    if (contentStates.some(({ isValid }) => !isValid)) {
      this.saveError.set(
        'Le contenu HTML du bandeau contient des erreurs. Corrigez les champs signalés puis réessayez.',
      );
      return;
    }

    this.saveError.set(null);

    await Promise.all(
      contentStates.map(
        async ({ filePath, normalizedContent }: BannerContentState): Promise<void> => {
          await this.persistenceService.putPublicFileText(filePath, normalizedContent);
        },
      ),
    );

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  public applyBookmark(bookmark: BannerBookmark): void {
    this.form.controls.frLogin.setValue(this.replaceDateToken(bookmark.languages.fr.login));
    this.form.controls.frApplication.setValue(this.replaceDateToken(bookmark.languages.fr.app));
    this.form.controls.frFreeMedispring.setValue(this.replaceDateToken(bookmark.languages.fr.free));
    this.form.controls.nlLogin.setValue(this.replaceDateToken(bookmark.languages.nl.login));
    this.form.controls.nlApplication.setValue(this.replaceDateToken(bookmark.languages.nl.app));
    this.form.controls.nlFreeMedispring.setValue(this.replaceDateToken(bookmark.languages.nl.free));
  }

  private applyValidationState(contentStates: BannerContentState[]): void {
    for (const contentState of contentStates) {
      const definition: BannerContentDefinition | undefined = this.bannerContentDefinitions.find(
        (candidate: BannerContentDefinition): boolean =>
          candidate.filePath === contentState.filePath,
      );

      if (!definition) {
        continue;
      }

      const control = this.form.controls[definition.controlName];
      control.setErrors(contentState.isValid ? null : { invalidHtml: true });

      if (contentState.isValid && control.value !== contentState.normalizedContent) {
        control.setValue(contentState.normalizedContent);
      }
    }
  }

  private validateAndNormalizeContent(content: string): Omit<BannerContentState, 'filePath' | 'content'> {
    const trimmedContent: string = content.trim();
    if (!trimmedContent) {
      return {
        isValid: true,
        normalizedContent: '',
      };
    }

    const wrappedContent: string = this.wrapWithDivIfNeeded(trimmedContent);
    const normalizedContent: string = this.ensureAnchorTargets(wrappedContent);

    if (this.isValidHtml(normalizedContent)) {
      return {
        isValid: true,
        normalizedContent,
      };
    }

    return {
      isValid: false,
      normalizedContent: content,
    };
  }

  private wrapWithDivIfNeeded(content: string): string {
    if (content.startsWith('<div>') && content.endsWith('</div>')) {
      return content;
    }

    return `<div>${content}</div>`;
  }

  private ensureAnchorTargets(content: string): string {
    return content.replace(/<a\b([^>]*)>/gi, (tag: string, attributes: string): string => {
      if (!/\bhref\s*=/.test(attributes) || /\btarget\s*=/.test(attributes)) {
        return tag;
      }

      return `<a${attributes} target="_blank">`;
    });
  }

  private isValidHtml(content: string): boolean {
    const parser: DOMParser = new DOMParser();
    const document: Document = parser.parseFromString(content, 'application/xhtml+xml');

    return !document.querySelector('parsererror');
  }

  private replaceDateToken(content: string): string {
    return content.replaceAll('{date}', this.getTodayInDayMonthFormat());
  }

  private getTodayInDayMonthFormat(): string {
    const today: Date = new Date();
    const day: string = String(today.getDate()).padStart(2, '0');
    const month: string = String(today.getMonth() + 1).padStart(2, '0');

    return `${day}/${month}`;
  }
}
